/**
 * Construcción de snippets para resultados de búsqueda.
 *
 * Dado el texto de un chunk y la consulta, extrae un fragmento centrado en el
 * primer match con el término envuelto en `<mark>`. El HTML resultante está
 * escapado salvo la propia marca `<mark>`, por lo que es seguro renderizarlo
 * con `v-html`.
 *
 * Módulo puro (sin I/O): testeable directamente.
 */

/**
 * Escapa los caracteres especiales de HTML en `s`, salvo `<mark>`/`</mark>`
 * que se preservan para el resaltado. Esta función espera recibir ya el texto
 * escapado y solo inyecta las marcas de forma controlada en `makeSnippet`.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Construye un snippet centrado en la primera ocurrencia de `query` dentro de
 * `text`.
 *
 * La coincidencia es insensible a mayúsculas/minúsculas. Devuelve HTML
 * escapado con la ocurrencia envuelta en `<mark>`. Si `query` no aparece, o
 * es demasiado corta (< 2 chars), devuelve `null` (el llamador decide qué
 * mostrar).
 *
 * @param radius Caracteres de contexto a cada lado del match (default 60).
 */
export function makeSnippet(
  text: string,
  query: string,
  radius = 60,
): string | null {
  const q = query.trim()
  if (q.length < 2) return null

  const lower = text.toLowerCase()
  const idx = lower.indexOf(q.toLowerCase())
  if (idx < 0) return null

  const matchEnd = idx + q.length
  const start = Math.max(0, idx - radius)
  const end = Math.min(text.length, matchEnd + radius)

  const before = text.slice(start, idx)
  const match = text.slice(idx, matchEnd)
  const after = text.slice(matchEnd, end)

  const prefix = start > 0 ? '…' : ''
  const suffix = end < text.length ? '…' : ''

  return `${prefix}${escapeHtml(before)}<mark>${escapeHtml(match)}</mark>${escapeHtml(after)}${suffix}`
}
